/**
 * Question (field) moves on a form skeleton (sections → subSections → fields), mirroring the form builder:
 * a move is `{ fieldId, sectionId, prevFieldId }` and places the field after `prevFieldId` in that
 * section (0 = first), which is exactly the body of the form builder's field reorder API.
 */

/** @returns {object|null} the section/sub-section with this id */
export const findSkeletonSection = (sections, sectionId) => {
	for (const section of sections ?? []) {
		if (section.id === sectionId) return section;
		const nested = findSkeletonSection(section.subSections, sectionId);
		if (nested) return nested;
	}
	return null;
};

/** @returns {number|null} id of the section/sub-section that holds the field */
export const findQuestionSectionId = (sections, fieldId) => {
	for (const section of sections ?? []) {
		if (section.fields?.some(field => field.id === fieldId)) return section.id;
		const nested = findQuestionSectionId(section.subSections, fieldId);
		if (nested != null) return nested;
	}
	return null;
};

const removeQuestion = (skeleton, fieldId) => {
	const rootIndex = skeleton.fields?.findIndex(field => field.id === fieldId) ?? -1;
	if (rootIndex >= 0) return skeleton.fields.splice(rootIndex, 1)[0];

	const walk = (sections) => {
		for (const section of sections ?? []) {
			const index = section.fields?.findIndex(field => field.id === fieldId) ?? -1;
			if (index >= 0) return section.fields.splice(index, 1)[0];
			const nested = walk(section.subSections);
			if (nested) return nested;
		}
		return null;
	};
	return walk(skeleton.sections);
};

/**
 * Applies one move to a copy of the skeleton. The field object itself is moved untouched, so its data,
 * settings, validations and rules are kept.
 * @returns {object|null} the new skeleton, or null when the field or target section no longer exists
 */
export const applyQuestionMove = (skeleton, { fieldId, sectionId, prevFieldId }) => {
	const next = JSON.parse(JSON.stringify(skeleton));
	if (!findSkeletonSection(next.sections, sectionId)) return null;

	const question = removeQuestion(next, fieldId);
	if (!question) return null;

	const target = findSkeletonSection(next.sections, sectionId);
	target.fields = target.fields ?? [];
	const prevIndex = prevFieldId ? target.fields.findIndex(field => field.id === prevFieldId) : -1;
	// An anchor that has gone away (deleted with its section) falls back to the end of the list
	const insertIndex = !prevFieldId ? 0 : (prevIndex >= 0 ? prevIndex + 1 : target.fields.length);
	target.fields.splice(insertIndex, 0, question);
	return next;
};

/**
 * Builds the move for dropping a question next to another question ('before' | 'after') or at the end
 * of a section ('end'). Returns null when the drop would leave the question where it already is.
 */
export const buildQuestionMove = (skeleton, fieldId, sectionId, anchorFieldId, position) => {
	if (fieldId === anchorFieldId) return null;

	const fromSectionId = findQuestionSectionId(skeleton?.sections, fieldId);
	const fromFields = findSkeletonSection(skeleton?.sections, fromSectionId)?.fields ?? [];
	const currentIndex = fromFields.findIndex(field => field.id === fieldId);
	const currentPrevFieldId = currentIndex > 0 ? fromFields[currentIndex - 1].id : 0;

	const targetFields = (findSkeletonSection(skeleton?.sections, sectionId)?.fields ?? [])
		.filter(field => field.id !== fieldId);
	let insertIndex = targetFields.length;
	if (position !== 'end') {
		const anchorIndex = targetFields.findIndex(field => field.id === anchorFieldId);
		if (anchorIndex >= 0) insertIndex = position === 'before' ? anchorIndex : anchorIndex + 1;
	}
	const prevFieldId = insertIndex > 0 ? targetFields[insertIndex - 1].id : 0;

	if (fromSectionId === sectionId && prevFieldId === currentPrevFieldId) return null;
	return { fieldId, sectionId, prevFieldId };
};

/**
 * Re-applies pending moves on top of a freshly loaded skeleton (e.g. after a section was created/removed).
 * @returns {{ skeleton: object, moves: object[] }} moves that could no longer be applied are dropped
 */
export const replayQuestionMoves = (skeleton, moves) => (moves ?? []).reduce((result, move) => {
	const next = applyQuestionMove(result.skeleton, move);
	return next ? { skeleton: next, moves: [...result.moves, move] } : result;
}, { skeleton, moves: [] });
